import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query/react';
import {
  AgeGroup,
  AGE_GROUPS,
  CreateJobConfigRequest,
  createJobConfigRequestSchema,
  DEFAULT_STEP_MODELS,
  JOB_APPROVALS,
  JOB_CONFIG_TARGET_LANGUAGES,
  JOB_TASK_COUNT_MAX,
  JobApproval,
  JobConfig,
  JobConfigTargetLanguages,
  ProblemCode,
  StepModelChoices,
  UpdateJobConfigRequest,
  updateJobConfigRequestSchema,
} from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { StepModelsField } from '../../job/StepModelsField';
import {
  useCreateJobConfigMutation,
  useDeleteJobConfigMutation,
  useGetJobConfigQuery,
  useUpdateJobConfigMutation,
} from '../jobConfigEndpoints';

/** The shape a zod issue needs for `mapIssues`; `ZodIssue` satisfies this (as `NewJobPage.tsx`). */
interface Issue {
  path: readonly PropertyKey[];
  message: string;
}

/**
 * `createJobConfigRequestSchema`'s and `updateJobConfigRequestSchema`'s top-level keys, mapped
 * to the form's field ids (QZ-D194, EXJ-D3), copied from `NewJobPage.tsx`'s `FIELD_IDS` rather
 * than shared, since that file is issue 08's.
 */
const FIELD_IDS: Record<string, string> = {
  jobConfigId: 'jobconfig-id',
  description: 'jobconfig-description',
  stepModels: 'jobconfig-step-models',
  approval: 'jobconfig-approval',
  targetLanguages: 'jobconfig-targetLanguages',
  ageGroups: 'jobconfig-ageGroups',
  maxTaskCount: 'jobconfig-maxTaskCount',
};

/** Maps the schema's issues to the form's field ids, keyed by `issue.path[0]` (as `NewJobPage.tsx`). */
const mapIssues = (issues: readonly Issue[]): Record<string, string[]> => {
  const fields: Record<string, string[]> = {};
  for (const issue of issues) {
    const id = FIELD_IDS[String(issue.path[0])];
    if (!id) {
      continue;
    }
    const list = fields[id] ?? [];
    if (!list.includes(issue.message)) {
      fields[id] = [...list, issue.message];
    }
  }
  return fields;
};

/** A field's issues, shown under it, as `NewJobPage.tsx`'s `FieldError` does. */
const FieldError = ({ id, messages }: { id: string; messages?: string[] }) => {
  if (!messages || messages.length === 0) {
    return null;
  }
  return (
    <div id={`${id}-error`} className="invalid-feedback d-block">
      {messages.map((message, index) => (
        <div key={index}>{message}</div>
      ))}
    </div>
  );
};

/**
 * `/job-configs/new` and `/job-configs/:jobConfigId` (QZ-D194, EXJ-D3). Loads the configuration
 * to edit, if any, showing `error.JobConfigNotFound` in place of the form on a 404. The form
 * itself is `JobConfigForm`, remounted with `key={jobConfig.jobConfigId}` once a configuration
 * has loaded, so its local state starts from the loaded values exactly once, as `TaskPage.tsx`'s
 * `TaskForm` does for a draft. Keying on the id rather than `updatedAt` matters: a save moves
 * `updatedAt`, and keying on it would remount the form on every save, losing the saved alert and
 * any further in-flight edits.
 */
export const JobConfigPage = () => {
  const { t } = useTranslation();
  const { jobConfigId } = useParams();
  const isNew = jobConfigId === undefined;

  const {
    data: jobConfig,
    error,
    isLoading,
  } = useGetJobConfigQuery(isNew ? skipToken : jobConfigId);

  const loadErrorCode = problemCode(error);
  const notFound =
    !jobConfig &&
    !!error &&
    (loadErrorCode === 'JobConfigNotFound' || (error as FetchBaseQueryError).status === 404);

  return (
    <main className="qz-main">
      <div className="qz-container is-narrow">
        <Link className="qz-back" to="/job-configs">
          <Icon name="arrow-left" /> {t('jobConfig.form.back')}
        </Link>

        <div className="qz-page-header">
          <div>
            <h1>{t(isNew ? 'jobConfig.form.newTitle' : 'jobConfig.form.editTitle')}</h1>
            <p className="qz-sub">{t('jobConfig.form.sub')}</p>
          </div>
        </div>

        {!isNew && isLoading && <p className="qz-muted">{t('job.page.loading')}</p>}

        {!isNew && !jobConfig && !isLoading && error && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>
              {t(
                notFound
                  ? 'error.JobConfigNotFound'
                  : loadErrorCode
                    ? `error.${loadErrorCode}`
                    : 'error.unknown'
              )}
            </div>
          </div>
        )}

        {(isNew || jobConfig) && (
          <JobConfigForm
            key={jobConfig?.jobConfigId ?? 'new'}
            isNew={isNew}
            jobConfig={jobConfig}
          />
        )}
      </div>
    </main>
  );
};

/** Props for `JobConfigForm`: `jobConfig` is set only when editing one that has loaded. */
interface JobConfigFormProps {
  isNew: boolean;
  jobConfig?: JobConfig;
}

const JobConfigForm = ({ isNew, jobConfig }: JobConfigFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createJobConfig, { isLoading: creating }] = useCreateJobConfigMutation();
  const [updateJobConfig, { isLoading: updating }] = useUpdateJobConfigMutation();
  const [deleteJobConfig, { isLoading: deleting }] = useDeleteJobConfigMutation();

  const [id, setId] = useState(jobConfig?.jobConfigId ?? '');
  const [description, setDescription] = useState(jobConfig?.description ?? '');
  const [stepModels, setStepModels] = useState<StepModelChoices>(
    jobConfig?.stepModels ?? DEFAULT_STEP_MODELS
  );
  const [approval, setApproval] = useState<JobApproval>(jobConfig?.approval ?? 'manual');
  const [targetLanguages, setTargetLanguages] = useState<JobConfigTargetLanguages>(
    jobConfig?.targetLanguages ?? 'job'
  );
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>(jobConfig?.ageGroups ?? [...AGE_GROUPS]);
  const [maxTaskCount, setMaxTaskCount] = useState(String(jobConfig?.maxTaskCount ?? 40));

  const [fields, setFields] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<ProblemCode | 'unknown' | undefined>(undefined);

  const toggleAgeGroup = (ageGroup: AgeGroup, checked: boolean) => {
    setAgeGroups((prev) => (checked ? [...prev, ageGroup] : prev.filter((a) => a !== ageGroup)));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFields({});
    setSubmitError(undefined);
    setSaved(false);

    const body: Record<string, unknown> = {
      description,
      stepModels,
      approval,
      targetLanguages,
      ageGroups,
      maxTaskCount: Number(maxTaskCount),
    };

    if (isNew) {
      const parsed = createJobConfigRequestSchema.safeParse({ jobConfigId: id, ...body });
      if (!parsed.success) {
        setFields(mapIssues(parsed.error.issues));
        return;
      }
      await submitCreate(parsed.data);
    } else {
      const parsed = updateJobConfigRequestSchema.safeParse(body);
      if (!parsed.success) {
        setFields(mapIssues(parsed.error.issues));
        return;
      }
      await submitUpdate(parsed.data);
    }
  };

  const submitCreate = async (data: CreateJobConfigRequest) => {
    try {
      const created = await createJobConfig(data).unwrap();
      navigate(`/job-configs/${encodeURIComponent(created.jobConfigId)}`);
    } catch (thrown) {
      const code = problemCode(thrown);
      if (code === 'JobConfigExists') {
        setFields({ [FIELD_IDS.jobConfigId]: [t('error.JobConfigExists')] });
        return;
      }
      setSubmitError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  const submitUpdate = async (data: UpdateJobConfigRequest) => {
    if (!jobConfig) {
      return;
    }
    try {
      await updateJobConfig({ id: jobConfig.jobConfigId, body: data }).unwrap();
      setSaved(true);
    } catch (thrown) {
      const code = problemCode(thrown);
      setSubmitError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  const handleDelete = async () => {
    if (!jobConfig) {
      return;
    }
    setDeleteError(undefined);
    try {
      await deleteJobConfig(jobConfig.jobConfigId).unwrap();
      navigate('/job-configs', { replace: true });
    } catch (thrown) {
      setDeleteError(problemCode(thrown) ?? 'unknown');
    }
  };

  const saving = creating || updating;

  return (
    <form className="qz-card" onSubmit={handleSubmit} noValidate>
      <div className="qz-card-body qz-stack">
        <div>
          <label htmlFor={FIELD_IDS.jobConfigId} className="form-label">
            {t('jobConfig.field.id')}
          </label>
          <input
            id={FIELD_IDS.jobConfigId}
            type="text"
            className={`form-control${fields[FIELD_IDS.jobConfigId] ? ' is-invalid' : ''}`}
            aria-describedby={
              fields[FIELD_IDS.jobConfigId] ? `${FIELD_IDS.jobConfigId}-error` : undefined
            }
            value={id}
            disabled={!isNew}
            onChange={(e) => setId(e.target.value)}
          />
          <div className="form-text">{t('jobConfig.field.idHint')}</div>
          <FieldError id={FIELD_IDS.jobConfigId} messages={fields[FIELD_IDS.jobConfigId]} />
        </div>

        <div>
          <label htmlFor={FIELD_IDS.description} className="form-label">
            {t('jobConfig.field.description')}
          </label>
          <textarea
            id={FIELD_IDS.description}
            rows={2}
            maxLength={500}
            className={`form-control${fields[FIELD_IDS.description] ? ' is-invalid' : ''}`}
            aria-describedby={
              fields[FIELD_IDS.description] ? `${FIELD_IDS.description}-error` : undefined
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FieldError id={FIELD_IDS.description} messages={fields[FIELD_IDS.description]} />
        </div>

        <StepModelsField
          value={stepModels}
          onChange={setStepModels}
          error={fields[FIELD_IDS.stepModels]?.[0]}
          disabled={saving}
        />

        <fieldset id={FIELD_IDS.approval}>
          <legend className="form-label">{t('job.approval.label')}</legend>
          <div className="qz-radio-cards">
            {JOB_APPROVALS.map((value) => (
              <div className="qz-radio-card" key={value}>
                <input
                  type="radio"
                  name="jobconfig-approval"
                  id={`jobconfig-approval-${value}`}
                  checked={approval === value}
                  onChange={() => setApproval(value)}
                />
                <label htmlFor={`jobconfig-approval-${value}`}>{t(`job.approval.${value}`)}</label>
              </div>
            ))}
          </div>
          <div className="form-text">{t('job.approval.hint')}</div>
          <FieldError id={FIELD_IDS.approval} messages={fields[FIELD_IDS.approval]} />
        </fieldset>

        <fieldset id={FIELD_IDS.targetLanguages}>
          <legend className="form-label">{t('jobConfig.field.targetLanguages')}</legend>
          <div className="qz-radio-cards">
            {JOB_CONFIG_TARGET_LANGUAGES.map((value) => (
              <div className="qz-radio-card" key={value}>
                <input
                  type="radio"
                  name="jobconfig-targetLanguages"
                  id={`jobconfig-targetLanguages-${value}`}
                  checked={targetLanguages === value}
                  onChange={() => setTargetLanguages(value)}
                />
                <label htmlFor={`jobconfig-targetLanguages-${value}`}>
                  {t(`jobConfig.targetLanguages.${value}`)}
                </label>
              </div>
            ))}
          </div>
          <FieldError id={FIELD_IDS.targetLanguages} messages={fields[FIELD_IDS.targetLanguages]} />
        </fieldset>

        <fieldset id={FIELD_IDS.ageGroups}>
          <legend className="form-label">{t('jobConfig.field.ageGroups')}</legend>
          <div className="qz-chips">
            {AGE_GROUPS.map((ageGroup) => (
              <span className="qz-chip" key={ageGroup}>
                <input
                  id={`jobconfig-ageGroups-${ageGroup}`}
                  type="checkbox"
                  checked={ageGroups.includes(ageGroup)}
                  onChange={(e) => toggleAgeGroup(ageGroup, e.target.checked)}
                />
                <label htmlFor={`jobconfig-ageGroups-${ageGroup}`}>
                  {t(`task.ageGroup.${ageGroup}`)}
                </label>
              </span>
            ))}
          </div>
          <FieldError id={FIELD_IDS.ageGroups} messages={fields[FIELD_IDS.ageGroups]} />
        </fieldset>

        <div>
          <label htmlFor={FIELD_IDS.maxTaskCount} className="form-label">
            {t('jobConfig.field.maxTaskCount')}
          </label>
          <input
            id={FIELD_IDS.maxTaskCount}
            type="number"
            min={1}
            max={JOB_TASK_COUNT_MAX}
            step={1}
            className={`form-control${fields[FIELD_IDS.maxTaskCount] ? ' is-invalid' : ''}`}
            aria-describedby={
              fields[FIELD_IDS.maxTaskCount] ? `${FIELD_IDS.maxTaskCount}-error` : undefined
            }
            value={maxTaskCount}
            onChange={(e) => setMaxTaskCount(e.target.value)}
          />
          <div className="form-text">{t('jobConfig.field.maxTaskCountHint')}</div>
          <FieldError id={FIELD_IDS.maxTaskCount} messages={fields[FIELD_IDS.maxTaskCount]} />
        </div>

        {saved && (
          <div className="alert alert-success">
            <Icon name="circle-check" /> {t('jobConfig.form.saved')}
          </div>
        )}

        {submitError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{submitError}</div>
          </div>
        )}

        {deleteError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{t(deleteError === 'unknown' ? 'error.unknown' : `error.${deleteError}`)}</div>
          </div>
        )}
      </div>

      <div className="qz-card-footer d-flex flex-wrap align-items-center gap-2">
        {!isNew &&
          (confirmingDelete ? (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="qz-small">{t('jobConfig.list.deleteConfirm')}</span>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleting}
                onClick={handleDelete}
              >
                {t('jobConfig.form.delete')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={deleting}
                onClick={() => setConfirmingDelete(false)}
              >
                {t('job.new.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => setConfirmingDelete(true)}
            >
              {t('jobConfig.form.delete')}
            </button>
          ))}
        <Link to="/job-configs" className="btn btn-outline-secondary ms-auto">
          {t('job.new.cancel')}
        </Link>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {t('jobConfig.form.save')}
        </button>
      </div>
    </form>
  );
};

export default JobConfigPage;
